import { Checkbox, FormControlLabel } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { TreeItem } from '@mui/x-tree-view';
import { DealProduct, Deductible, Plan, ProductPlan, Provider, Rate } from '../interfaces';
import { SelectedRetails } from './EditProductModal';

interface IDeductibleTreeItem {
	rate: Rate;
	option: Deductible;
	provider: Provider;
	plan: Plan;
	product: ProductPlan;
	selectedRetails: SelectedRetails;
	setSelectedRetails: (retails: SelectedRetails) => void;
	setIsDeductiblesSelected: (bool: boolean) => void;
	isDeductiblesSelected: boolean;
	dealProducts?: DealProduct[];
	type?: string;
}

const DeductibleTreeItem = ({
	provider,
	plan,
	product,
	option,
	rate,
	selectedRetails,
	setSelectedRetails,
	setIsDeductiblesSelected,
	isDeductiblesSelected,
	dealProducts,
	type,
}: IDeductibleTreeItem) => {
	const { deductiblesSelected, optionSelected, surchargeSelected, dealsProductsMap, removedProducts } = selectedRetails;
	const productId = type === 'dealCalculator' ? 'dealCalculatorLeadProductId' : 'dealProductId';

	const DEAL_PRODUCTS = useMemo(() => (
		dealProducts?.map((dealProduct: DealProduct) => {
			return {
				...dealProduct, localId: dealProduct.rate.rateId + dealProduct.deductible.id
			};
		})
	), [dealProducts]);

	const localId = rate.rateId + option.deductibleId;
	const selectedDeductible = deductiblesSelected.get(product.productTypeId);

	const currentDealProduct = dealsProductsMap.get(product.productTypeId);
	const currentDealProductId = (currentDealProduct?.rate?.rateId || '') + currentDealProduct?.deductible.id;

	const isSelectedDeductible =
		localId === currentDealProductId ||
		localId === (selectedDeductible?.rate?.rateId || '') + (selectedDeductible?.option?.deductibleId || '');

	useEffect(() => {
		if (!isSelectedDeductible) {
			optionSelected.forEach((__value, key: string) => {
				if (key.includes(rate.rateId)) {
					optionSelected.delete(key);
				}
			});
		}
	}, [isSelectedDeductible]);

  useEffect(() => {
    const selectedProduct = dealsProductsMap.get(product.productTypeId);

    if (selectedProduct?.rate.rateId) {
      if (localId === (selectedProduct?.rate.rateId + selectedProduct.deductible.id)) {
        deductiblesSelected.set(selectedProduct.productTypeId, {
          rate,
          provider,
          plan,
          option,
          product,
        });
      }
      setSelectedRetails({ ...selectedRetails });
    }
  }, [dealsProductsMap, localId, deductiblesSelected]);

	const handleChange = useCallback((__event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		const id = DEAL_PRODUCTS?.find(dealProduct => ((dealProduct.localId === localId) || (dealProduct.productTypeId === product.productTypeId)))?.[productId];

		if (!removedProducts.has(product.productTypeId) && !checked) {
			removedProducts.set(product.productTypeId, { productTypeId: product.productTypeId, [productId]: id, rateId: rate.rateId });
		} else if (removedProducts.has(product.productTypeId) && !isSelectedDeductible) {
			removedProducts.delete(product.productTypeId);
		}

		if (dealsProductsMap.has(product.productTypeId)) {
			dealsProductsMap.delete(product.productTypeId);
			deductiblesSelected.delete(product.productTypeId);
		}

		if (deductiblesSelected.has(product.productTypeId)) {
			if (isSelectedDeductible) {
				deductiblesSelected.delete(product.productTypeId);
			} else {
				deductiblesSelected.set(product.productTypeId, {
					rate,
					provider,
					plan,
					option,
					product,
					[productId]: id,
				});
			}
		} else if (checked) {
			deductiblesSelected.set(product.productTypeId, {
				provider,
				plan,
				rate,
				option,
				product,
				[productId]: id,
			});
		}
		
		setIsDeductiblesSelected(!isDeductiblesSelected);
		setSelectedRetails({ ...selectedRetails });
	}, [
		DEAL_PRODUCTS,
		localId,
		productId,
		deductiblesSelected,
		product,
		isSelectedDeductible,
		optionSelected,
		surchargeSelected,
		rate,
		provider,
		plan,
		option,
		product,
		dealsProductsMap
	]);

	return (
		<TreeItem
			itemId={localId}
			label={
				<FormControlLabel
					key={localId}
					label={option.deductibleDescription}
					control={
						<Checkbox
							size="small"
							checked={isSelectedDeductible}
							onChange={handleChange}
						/>
					}
				/>
			}
		/>
	);
};

export default DeductibleTreeItem;
